/**
 * Split-chart layouts inside one chart module (owner request 2026-09-27: "split chart view similar
 * to TradingView and Tradesea"; SPEC §12.9 grid presets). Pure: the web app's `ChartPanel` renders
 * it, node:test covers it. Stored in the chart panel's dockview params (`chartLayout`), so it
 * travels with the workspace document; a panel without it is a plain chart (layout "1").
 *
 * Symbol rules with link groups (documented in chart-engine.md "Split charts"):
 * - The **active pane** (highlighted) is the module's member of its link group: it always shows
 *   the group's symbol, and a symbol typed into it retargets the group.
 * - Other panes keep their own stored symbol. Activating a pane stores the old active pane's symbol
 *   in it and publishes the new active pane's symbol to the group (so the DOM / order pad on the
 *   same colour follow the chart you are looking at).
 * - **Symbol sync on:** every pane shows one symbol — the group's (linked) or the active pane's
 *   (unlinked); a symbol typed into any pane changes all of them (and the group, when linked).
 * - Unlinked module, sync off: every pane has its own symbol, nothing is published.
 */

/** Pane tree: a leaf is a pane index; a node lays its children out in a row (left → right) or col. */
export type SplitNode = number | { dir: "row" | "col"; children: SplitNode[] };

export type ChartLayoutId =
  | "1"
  | "2-side"
  | "2-stack"
  | "3-side"
  | "3-stack"
  | "3-left"
  | "3-top"
  | "4-grid"
  | "4-left"
  | "6-wide"
  | "6-tall"
  | "8-grid";

export type ChartLayoutPreset = { id: ChartLayoutId; name: string; count: number; tree: SplitNode };

const row = (...children: SplitNode[]): SplitNode => ({ dir: "row", children });
const col = (...children: SplitNode[]): SplitNode => ({ dir: "col", children });

/** Picker order (TradingView "Select layout" groups by pane count). */
export const CHART_LAYOUTS: readonly ChartLayoutPreset[] = [
  { id: "1", name: "1 chart", count: 1, tree: 0 },
  { id: "2-side", name: "2 side by side", count: 2, tree: row(0, 1) },
  { id: "2-stack", name: "2 stacked", count: 2, tree: col(0, 1) },
  { id: "3-side", name: "3 columns", count: 3, tree: row(0, 1, 2) },
  { id: "3-stack", name: "3 rows", count: 3, tree: col(0, 1, 2) },
  { id: "3-left", name: "1 large left + 2", count: 3, tree: row(0, col(1, 2)) },
  { id: "3-top", name: "1 large top + 2", count: 3, tree: col(0, row(1, 2)) },
  { id: "4-grid", name: "2 × 2", count: 4, tree: col(row(0, 1), row(2, 3)) },
  { id: "4-left", name: "1 large left + 3", count: 4, tree: row(0, col(1, 2, 3)) },
  { id: "6-wide", name: "3 × 2", count: 6, tree: col(row(0, 1, 2), row(3, 4, 5)) },
  { id: "6-tall", name: "2 × 3", count: 6, tree: col(row(0, 1), row(2, 3), row(4, 5)) },
  { id: "8-grid", name: "4 × 2", count: 8, tree: col(row(0, 1, 2, 3), row(4, 5, 6, 7)) },
];

export const MAX_CHART_PANES = 8;

const PRESETS = new Map<string, ChartLayoutPreset>(CHART_LAYOUTS.map((p) => [p.id, p]));

export function chartLayoutPreset(id: string): ChartLayoutPreset {
  return PRESETS.get(id) ?? (CHART_LAYOUTS[0] as ChartLayoutPreset);
}

/** TradingView-style sync toggles, per chart module. All off by default (as in TradingView). */
export type ChartSync = {
  symbol: boolean;
  /** Timeframe / bar type (and the range phantom setting that belongs to it). */
  interval: boolean;
  crosshair: boolean;
  /** Visible time range: scroll and zoom. */
  time: boolean;
  /** Date-range jumps: go live (End / double-click price axis), Home. */
  dateRange: boolean;
};

export const NO_SYNC: ChartSync = {
  symbol: false,
  interval: false,
  crosshair: false,
  time: false,
  dateRange: false,
};

export type ChartPaneSpec = {
  id: string;
  /** Own symbol (null = none yet). The active pane of a linked module shows the group's instead. */
  symbol: string | null;
  /** Canonical timeframe string ("1m", "500t", "8r"…). */
  tf: string;
  /** Range bars: phantom gap-fillers; absent = on (Q-T5). */
  rangePhantoms?: boolean;
  /**
   * This pane's indicators + indicator panes (`ChartIndicatorsState`, `@helm/core/indicators`,
   * ADR-0018). Opaque here and treated as immutable; sanitised by the chart when read. Absent =
   * no indicators.
   */
  indicators?: unknown;
  /**
   * This pane's chart overlays (`Partial<OverlayOptions>`, `@helm/chart`, ADR-0019: heatmap, key
   * levels, stats box, position/order lines). Opaque here; missing keys take the chart defaults.
   */
  overlays?: unknown;
  /**
   * This pane's chart settings (`ChartSettings`, `@helm/chart` chartSettings.ts, ADR-0026: chart
   * style and colours, status line, price scale mode / placement, appearance). Opaque here;
   * sanitised by the chart when read. Absent = the user's saved default (or the factory settings).
   */
  chartSettings?: unknown;
  /**
   * This pane's compare / overlay symbols (`CompareState`, compare.ts: other symbols, spreads and
   * ratios drawn over the main series). Opaque here; sanitised by the chart when read. Absent =
   * none.
   */
  compare?: unknown;
};

/**
 * Per-pane add-ons that also exist as top-level panel params (legacy builds or template seeds),
 * read into pane 0.
 */
const PANE_ADDONS = ["indicators", "overlays", "chartSettings", "compare"] as const;
type PaneAddon = (typeof PANE_ADDONS)[number];

export type ChartLayoutState = {
  layout: ChartLayoutId;
  /** Pane specs; more than the layout shows are kept, so 4 → 2 → 4 restores the other two. */
  panes: ChartPaneSpec[];
  /** Index of the active pane (always < the layout's count). */
  active: number;
  /** Splitter ratios by `${layout}:${path}` (fractions summing to 1); missing = equal. */
  ratios: Record<string, number[]>;
  sync: ChartSync;
};

/** The chart fields of `WidgetParams` this model reads and writes. */
export type ChartLayoutParams = {
  tf?: string;
  rangePhantoms?: boolean;
  chartLayout?: unknown;
  /** Legacy top-level indicators (indicator framework before split charts): read into pane 0. */
  indicators?: unknown;
  /** Legacy top-level overlays (Glacis overlays before split charts; template seed): pane 0. */
  overlays?: unknown;
  /** Template seed: chart settings for pane 0 (ADR-0026). */
  chartSettings?: unknown;
  /** Template seed: compare symbols for pane 0 (chart templates). */
  compare?: unknown;
};

export const DEFAULT_CHART_TF = "1m";

/** Link-group context of the module. */
export type LinkContext = { linked: boolean; symbol: string | null };

// ------------------------------------------------------------------------------------------------
// Persistence

/** "nq " → "NQ"; null when empty or implausible (the gateway resolves validity). */
export function normalizeSymbol(s: unknown): string | null {
  if (typeof s !== "string") return null;
  const t = s.trim().toUpperCase();
  return /^[A-Z0-9][A-Z0-9.:/_-]{0,31}$/.test(t) ? t : null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function sanitizeRatio(v: unknown): number[] | null {
  if (!Array.isArray(v) || v.length < 2 || v.length > MAX_CHART_PANES) return null;
  const xs = v.map((x) => (typeof x === "number" && Number.isFinite(x) && x > 0 ? x : NaN));
  if (xs.some((x) => Number.isNaN(x))) return null;
  const sum = xs.reduce((a, b) => a + b, 0);
  return xs.map((x) => x / sum);
}

/**
 * Read the chart state from panel params. Missing or unreadable `chartLayout` = a plain chart:
 * layout "1" with one pane built from the top-level `tf` / `rangePhantoms` (migration of every
 * chart saved before split layouts existed).
 */
export function readChartLayout(params: ChartLayoutParams): ChartLayoutState {
  const tf = typeof params.tf === "string" && params.tf ? params.tf : DEFAULT_CHART_TF;
  const plainPane: ChartPaneSpec = { id: "p1", symbol: null, tf };
  if (typeof params.rangePhantoms === "boolean") plainPane.rangePhantoms = params.rangePhantoms;
  for (const k of PANE_ADDONS) if (hasAddon(params[k])) plainPane[k] = params[k];
  const raw = params.chartLayout;
  if (!isRecord(raw)) return { layout: "1", panes: [plainPane], active: 0, ratios: {}, sync: { ...NO_SYNC } };

  const preset = chartLayoutPreset(typeof raw["layout"] === "string" ? raw["layout"] : "1");
  const seen = new Set<string>();
  const panes: ChartPaneSpec[] = [];
  if (Array.isArray(raw["panes"])) {
    for (const p of raw["panes"]) {
      if (!isRecord(p) || panes.length >= MAX_CHART_PANES) continue;
      let id = typeof p["id"] === "string" && p["id"] ? p["id"] : "";
      if (!id || seen.has(id)) id = nextPaneId(panes);
      seen.add(id);
      const pane: ChartPaneSpec = {
        id,
        symbol: normalizeSymbol(p["symbol"]),
        tf: typeof p["tf"] === "string" && p["tf"] ? p["tf"] : tf,
      };
      if (typeof p["rangePhantoms"] === "boolean") pane.rangePhantoms = p["rangePhantoms"];
      for (const k of PANE_ADDONS) if (hasAddon(p[k])) pane[k] = p[k];
      panes.push(pane);
    }
  }
  if (panes.length === 0) panes.push(plainPane);
  // Legacy top-level indicators / overlays belong to the first pane (it was the only chart).
  const first = panes[0];
  if (first)
    for (const k of PANE_ADDONS) if (first[k] === undefined && hasAddon(params[k])) first[k] = params[k];
  while (panes.length < preset.count)
    panes.push({ ...cloneSpec(panes[panes.length - 1] ?? plainPane), id: nextPaneId(panes) });

  const ratios: Record<string, number[]> = {};
  if (isRecord(raw["ratios"])) {
    for (const [k, v] of Object.entries(raw["ratios"])) {
      const r = sanitizeRatio(v);
      if (r) ratios[k] = r;
    }
  }
  const sync: ChartSync = { ...NO_SYNC };
  if (isRecord(raw["sync"])) {
    for (const k of Object.keys(NO_SYNC) as (keyof ChartSync)[]) sync[k] = raw["sync"][k] === true;
  }
  const a = typeof raw["active"] === "number" && Number.isInteger(raw["active"]) ? raw["active"] : 0;
  return { layout: preset.id, panes, active: Math.min(Math.max(0, a), preset.count - 1), ratios, sync };
}

/** A stored add-on value worth keeping (anything but null / undefined). */
function hasAddon(v: unknown): boolean {
  return v !== undefined && v !== null;
}

/**
 * A chart that is exactly what an old single-chart panel could express (no indicators or overlays
 * either).
 */
export function isPlainChart(s: ChartLayoutState): boolean {
  return (
    s.layout === "1" &&
    s.panes.length === 1 &&
    s.panes[0]?.symbol === null &&
    PANE_ADDONS.every((k) => s.panes[0]?.[k] === undefined) &&
    Object.keys(s.ratios).length === 0 &&
    !Object.values(s.sync).some(Boolean)
  );
}

/**
 * Params patch for `state`: `chartLayout` (absent for a plain chart, so single charts are saved
 * exactly as before) plus top-level `tf` / `rangePhantoms` mirrored from the active pane (older
 * builds reading the document show that pane).
 */
export function writeChartLayout(s: ChartLayoutState): {
  tf: string;
  rangePhantoms?: boolean;
  chartLayout?: {
    layout: ChartLayoutId;
    panes: ChartPaneSpec[];
    active: number;
    ratios: Record<string, number[]>;
    sync: ChartSync;
  };
} {
  const a = activePane(s);
  const out: ReturnType<typeof writeChartLayout> = { tf: a.tf };
  if (a.rangePhantoms !== undefined) out.rangePhantoms = a.rangePhantoms;
  if (!isPlainChart(s)) {
    out.chartLayout = {
      layout: s.layout,
      panes: s.panes.map(cloneSpec),
      active: s.active,
      ratios: Object.fromEntries(Object.entries(s.ratios).map(([k, v]) => [k, [...v]])),
      sync: { ...s.sync },
    };
  }
  return out;
}

/**
 * Apply a write to panel params: replaces the chart fields, removing `chartLayout` /
 * `rangePhantoms` when the write has none (so a split chart set back to plain saves as before),
 * and the legacy top-level `indicators` / `overlays` (the panes carry them now).
 */
export function applyChartLayoutParams<P extends ChartLayoutParams>(params: P, s: ChartLayoutState): P {
  const next: P = { ...params };
  delete next.chartLayout;
  delete next.rangePhantoms;
  delete next.indicators;
  delete next.overlays;
  delete next.chartSettings;
  delete next.compare;
  return { ...next, ...writeChartLayout(s) };
}

function cloneSpec(p: ChartPaneSpec): ChartPaneSpec {
  const c: ChartPaneSpec = { id: p.id, symbol: p.symbol, tf: p.tf };
  if (p.rangePhantoms !== undefined) c.rangePhantoms = p.rangePhantoms;
  for (const k of PANE_ADDONS) if (p[k] !== undefined) c[k] = p[k];
  return c;
}

function nextPaneId(panes: readonly { id: string }[]): string {
  const used = new Set(panes.map((p) => p.id));
  for (let i = 1; ; i++) if (!used.has(`p${String(i)}`)) return `p${String(i)}`;
}

export function activePane(s: ChartLayoutState): ChartPaneSpec {
  return s.panes[s.active] ?? (s.panes[0] as ChartPaneSpec);
}

export function visibleCount(s: ChartLayoutState): number {
  return chartLayoutPreset(s.layout).count;
}

// ------------------------------------------------------------------------------------------------
// Geometry

export type Rect = { x: number; y: number; w: number; h: number };
export type PaneRect = Rect & { index: number };
/** A draggable boundary between child `index` and `index + 1` of the node at `key`. */
export type SplitterRect = Rect & { key: string; index: number; dir: "row" | "col"; node: Rect };

/** Ratios for a node with `n` children: stored ones when they fit, else equal. */
export function ratiosFor(ratios: Record<string, number[]>, key: string, n: number): number[] {
  const r = ratios[key];
  return r && r.length === n ? r : Array.from({ length: n }, () => 1 / n);
}

/** Pane and splitter rectangles in fractions of the module body (0..1). */
export function layoutRects(
  layout: string,
  ratios: Record<string, number[]> = {},
): { panes: PaneRect[]; splitters: SplitterRect[] } {
  const preset = chartLayoutPreset(layout);
  const panes: PaneRect[] = [];
  const splitters: SplitterRect[] = [];
  const walk = (n: SplitNode, r: Rect, path: string): void => {
    if (typeof n === "number") {
      panes.push({ ...r, index: n });
      return;
    }
    const key = `${preset.id}:${path}`;
    const rs = ratiosFor(ratios, key, n.children.length);
    let off = 0;
    n.children.forEach((c, i) => {
      const f = rs[i] ?? 0;
      const cr: Rect =
        n.dir === "row"
          ? { x: r.x + off * r.w, y: r.y, w: f * r.w, h: r.h }
          : { x: r.x, y: r.y + off * r.h, w: r.w, h: f * r.h };
      walk(c, cr, path === "" ? String(i) : `${path}.${String(i)}`);
      off += f;
      if (i < n.children.length - 1) {
        splitters.push(
          n.dir === "row"
            ? { key, index: i, dir: "row", node: r, x: r.x + off * r.w, y: r.y, w: 0, h: r.h }
            : { key, index: i, dir: "col", node: r, x: r.x, y: r.y + off * r.h, w: r.w, h: 0 },
        );
      }
    });
  };
  walk(preset.tree, { x: 0, y: 0, w: 1, h: 1 }, "");
  panes.sort((a, b) => a.index - b.index);
  return { panes, splitters };
}

/**
 * Move the boundary after child `index` by `delta` (fraction of the node). Only the two adjacent
 * children change; both stay at least `minFrac` (capped at half their combined size).
 */
export function moveSplitter(
  ratios: readonly number[],
  index: number,
  delta: number,
  minFrac: number,
): number[] {
  const out = [...ratios];
  const a = out[index];
  const b = out[index + 1];
  if (a === undefined || b === undefined || !Number.isFinite(delta)) return out;
  const pair = a + b;
  const min = Math.min(Math.max(0, minFrac), pair / 2);
  const na = Math.min(pair - min, Math.max(min, a + delta));
  out[index] = na;
  out[index + 1] = pair - na;
  return out;
}

/** Store new ratios for a splitter key. */
export function setRatios(s: ChartLayoutState, key: string, ratios: number[]): ChartLayoutState {
  return { ...s, ratios: { ...s.ratios, [key]: ratios } };
}

/** Forget the ratios of the current layout (double-click a splitter: equal sizes). */
export function resetRatios(s: ChartLayoutState, key?: string): ChartLayoutState {
  const prefix = `${s.layout}:`;
  const ratios = Object.fromEntries(
    Object.entries(s.ratios).filter(([k]) => (key === undefined ? !k.startsWith(prefix) : k !== key)),
  );
  return { ...s, ratios };
}

// ------------------------------------------------------------------------------------------------
// Layout, panes, sync

/**
 * Switch layout. New panes copy the active pane (TradingView behaviour): its timeframe and bar
 * settings, its indicators and overlays, and its *displayed* symbol (`activeSymbol`, the group's
 * when linked).
 */
export function setLayout(s: ChartLayoutState, id: string, activeSymbol: string | null): ChartLayoutState {
  const preset = chartLayoutPreset(id);
  const panes = s.panes.map(cloneSpec);
  const src = activePane(s);
  while (panes.length < preset.count) {
    panes.push({ ...cloneSpec(src), id: nextPaneId(panes), symbol: activeSymbol ?? src.symbol });
  }
  return { ...s, layout: preset.id, panes, active: Math.min(s.active, preset.count - 1) };
}

/** Symbol a pane shows (see the module doc comment for the rules). */
export function paneSymbol(s: ChartLayoutState, i: number, link: LinkContext): string | null {
  if (link.linked && (s.sync.symbol || i === s.active)) return link.symbol;
  if (s.sync.symbol) return activePane(s).symbol;
  return s.panes[i]?.symbol ?? null;
}

/**
 * Make pane `i` active. Linked and symbol sync off: the old active pane keeps the group's symbol
 * as its own, and the new one's symbol is published to the group (`publish`, null = nothing).
 */
export function activatePane(
  s: ChartLayoutState,
  i: number,
  link: LinkContext,
): { state: ChartLayoutState; publish: string | null } {
  if (i === s.active || i < 0 || i >= visibleCount(s)) return { state: s, publish: null };
  if (!link.linked || s.sync.symbol) return { state: { ...s, active: i }, publish: null };
  const panes = s.panes.map(cloneSpec);
  const old = panes[s.active];
  if (old && link.symbol !== null) old.symbol = link.symbol;
  const target = panes[i]?.symbol ?? null;
  return {
    state: { ...s, panes, active: i },
    publish: target !== null && target !== link.symbol ? target : null,
  };
}

/**
 * A symbol typed into pane `i`. Stored on the pane (all panes with symbol sync); published to the
 * group when the module is linked and the pane is the active one (or symbol sync is on).
 */
export function setPaneSymbol(
  s: ChartLayoutState,
  i: number,
  symbol: string,
  link: LinkContext,
): { state: ChartLayoutState; publish: string | null } {
  const sym = normalizeSymbol(symbol);
  if (sym === null || !s.panes[i]) return { state: s, publish: null };
  const panes = s.panes.map((p, k) =>
    s.sync.symbol || k === i ? { ...cloneSpec(p), symbol: sym } : cloneSpec(p),
  );
  const publish = link.linked && (s.sync.symbol || i === s.active) ? sym : null;
  return { state: { ...s, panes }, publish };
}

/** Timeframe for pane `i` (every pane with interval sync). */
export function setPaneTimeframe(s: ChartLayoutState, i: number, tf: string): ChartLayoutState {
  if (!s.panes[i]) return s;
  return {
    ...s,
    panes: s.panes.map((p, k) => (s.sync.interval || k === i ? { ...cloneSpec(p), tf } : cloneSpec(p))),
  };
}

/**
 * Indicators for pane `i` only (never synced across panes: each chart keeps its own studies, as in
 * TradingView). null / undefined removes them.
 */
export function setPaneIndicators(s: ChartLayoutState, i: number, indicators: unknown): ChartLayoutState {
  return setPaneAddon(s, i, "indicators", indicators);
}

/** Overlay switches for pane `i` only (heatmap, key levels, …; ADR-0019). null removes them. */
export function setPaneOverlays(s: ChartLayoutState, i: number, overlays: unknown): ChartLayoutState {
  return setPaneAddon(s, i, "overlays", overlays);
}

/**
 * Chart settings for pane `i` only (style, scale, appearance; ADR-0026 — never synced across
 * panes, like TradingView's per-chart settings). null removes them: the pane shows the default.
 */
export function setPaneChartSettings(s: ChartLayoutState, i: number, settings: unknown): ChartLayoutState {
  return setPaneAddon(s, i, "chartSettings", settings);
}

/** Compare symbols for pane `i` only (compare.ts). null / an empty state removes them. */
export function setPaneCompare(s: ChartLayoutState, i: number, compare: unknown): ChartLayoutState {
  return setPaneAddon(s, i, "compare", compare);
}

function setPaneAddon(s: ChartLayoutState, i: number, key: PaneAddon, value: unknown): ChartLayoutState {
  if (!s.panes[i]) return s;
  return {
    ...s,
    panes: s.panes.map((p, k) => {
      if (k !== i) return cloneSpec(p);
      // cloneSpec copies only the add-ons that are set, so dropping one is leaving it out.
      const c = cloneSpec({ ...p, [key]: undefined });
      if (hasAddon(value)) c[key] = value;
      return c;
    }),
  };
}

/** Range phantom setting for pane `i` (it belongs to the bar type: every pane with interval sync). */
export function setPaneRangePhantoms(s: ChartLayoutState, i: number, on: boolean): ChartLayoutState {
  if (!s.panes[i]) return s;
  return {
    ...s,
    panes: s.panes.map((p, k) =>
      s.sync.interval || k === i ? { ...cloneSpec(p), rangePhantoms: on } : cloneSpec(p),
    ),
  };
}

/**
 * Change sync flags. Turning interval sync on aligns every pane to the active pane's timeframe;
 * turning symbol sync on or off stores the symbol every pane shows at that moment
 * (`activeSymbol`), so nothing jumps.
 */
export function setSync(
  s: ChartLayoutState,
  patch: Partial<ChartSync>,
  activeSymbol: string | null,
): ChartLayoutState {
  const sync = { ...s.sync, ...patch };
  let panes = s.panes.map(cloneSpec);
  const a = activePane(s);
  if (sync.interval && !s.sync.interval) {
    panes = panes.map((p) => {
      const c: ChartPaneSpec = { ...p, tf: a.tf };
      if (a.rangePhantoms !== undefined) c.rangePhantoms = a.rangePhantoms;
      else delete c.rangePhantoms;
      return c;
    });
  }
  if (sync.symbol !== s.sync.symbol && (sync.symbol || s.sync.symbol)) {
    const sym = activeSymbol ?? a.symbol;
    if (sym !== null) panes = panes.map((p) => ({ ...p, symbol: sym }));
  }
  return { ...s, panes, sync };
}

export type SyncKind = "crosshair" | "time" | "dateRange";

/** Visible panes that follow a crosshair / time / date-range event from pane `source`. */
export function syncTargets(s: ChartLayoutState, source: number, kind: SyncKind): number[] {
  if (!s.sync[kind]) return [];
  const n = visibleCount(s);
  if (source < 0 || source >= n) return [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (i !== source) out.push(i);
  return out;
}
